// Key under which we store the flag in sessionStorage
const MARKETING_REMINDER_KEY = 'marketingEventReminder';

/**
 * Mark that the user has seen the marketing event reminder.
 */
export function markMarketingReminderAsSeen() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(MARKETING_REMINDER_KEY, 'true');
}

/**
 * Check whether the user has already seen the marketing event reminder.
 * @returns {boolean} true if the reminder flag is set in sessionStorage
 */
export function hasSeenMarketingReminder() {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(MARKETING_REMINDER_KEY) === 'true';
}
/**
 * Helper function to fetch address details using Mapbox reverse geocoding
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<{addressLine1: string, town: string, countryIso: string, postcode: string}>}
 */
export async function fetchAddressFromMapbox(lat, lng) {
  // Default address in case reverse geocoding fails
  const defaultAddress = {
    addressLine1: 'New York',
    town: 'Austin',
    countryIso: 'US',
    postcode: '212932',
  };
  const BASE_URL = process.env.REACT_APP_MAPBOX_GEOCODING_URL;
  const TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  const TYPES_LIST = process.env.REACT_APP_MAPBOX_GEOCODING_TYPES;
  if (!lat || !lng) return defaultAddress;

  try {
    const accessToken = TOKEN;
    if (!accessToken) {
      console.error('Mapbox access token is missing');
      return defaultAddress;
    }

    const response = await fetch(
      `${BASE_URL}/${lng},${lat}.json?access_token=${accessToken}&types=${TYPES_LIST}`
    );
    const data = await response.json();

    if (data.features && data.features.length > 0) {
      const feature = data.features[0];
      const placeName = feature.place_name || '';
      const context = feature.context || [];

      // Extract address components
      const addressLine1 = feature.address || placeName.split(',')[0] || 'Unknown';
      const town = context.find(c => c.id.includes('place'))?.text || 'Unknown';
      const country =
        context.find(c => c.id.includes('country'))?.short_code?.toUpperCase() || 'MX';
      const postcode = context.find(c => c.id.includes('postcode'))?.text || 'Unknown';

      return {
        addressLine1,
        town,
        countryIso: country,
        postcode,
      };
    }

    return defaultAddress;
  } catch (error) {
    console.error('Failed to fetch address from Mapbox:', error);
    return defaultAddress;
  }
}
